/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { IImageSettings, getUrlSync, msdyn365Commerce, TelemetryEvent } from '@msdyn365-commerce/core';
import { ICartViewProps } from '@msdyn365-commerce-modules/cart';
import { ICartIconViewProps } from '@msdyn365-commerce-modules/cart/src/modules/cart-icon/./cart-icon';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    Button,
    getPayloadObject,
    INodeProps,
    KeyCodes,
    Modal,
    Node,
    onTelemetryClick,
    ITelemetryContent,
    getTelemetryAttributes,
    getTelemetryObject,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';
import { CartIconComponent } from './components/carticon.component';
import { FlyoutCartLineItems, IFlyoutCartLineItemViewProps } from './components/flyout-cart-line-items';
import { OrgUnitLocation, CartLine, LineDataValidationFailure, ChannelDeliveryOptionConfiguration } from '@msdyn365-commerce/retail-proxy';
import { validateCartAsync } from '@msdyn365-commerce-modules/cart-utilities';
import { reaction } from 'mobx';
import { getMiniCartData, IMiniCartData } from './utils/get-products-in-mini-cart';

import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';
import { COLLECTIONS_SITE_LANGUAGE, ERROR_OR_NOTIFICATION, HEADER_FOOTER, KWR, MAORI_LANG } from '../../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../../utilities/language-change';

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
    miniCartData: IMiniCartData;
    orgUnitLocations?: OrgUnitLocation[];
    isQuantityLimitsFeatureEnabled?: boolean;
    isCartValid?: boolean;
    viewCartEmptyMsg: boolean;
    preferredLanguage: string;
    preferredLanguageInError: string;
    siteLang: string;
}

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): JSX.Element[] | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }
    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;
        console.log('Cart icon view - renderCartlines loop:', cartLine);
        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLine.cartline}</Node>
                {/* {props.slots?.quickview &&
                    cartLine!?.data?.product?.RecordId &&
                    renderQuickView(props.slots?.quickview, cartLine!?.data?.product?.RecordId)} */}
                {cartLine.remove}
                {/* {cartLine.storeLocation} */}
            </Node>
        );
    });
};

/**
//  * Gets the react node for  product description.
//  * @param  quickview - Quick view node.
//  * @param  item - Product id to de displayed in quickview.
//  * @returns The product quickview component.
//  */
// function renderQuickView(quickview: React.ReactNode, item: number): JSX.Element | undefined {
//     if (quickview === null) {
//         return undefined;
//     }
//     return React.cloneElement(quickview as React.ReactElement);
// }

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
// const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
//     const cart = props.data.cart.result ?? undefined;
//     const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
//     const totalPrice = price
//         ? props.context.cultureFormatter.formatCurrency(price)
//         : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);
//     return (
//         <div className='ms-cart-icon__subTotalText'>
//             <span className='ms-cart-icon__subTotalPriceText'>
//                 {props.resources.totalPriceFormatString}
//                 <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
//             </span>
//         </div>
//     );
// };

enum cartLinesSortOrder {
    ascending = 'ascending',
    descending = 'descending'
}

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>,
    ICartIconViewState
> {
    private isAutoDisplayTriggered: boolean;

    private lastValidatedCartVersion: number | undefined;

    private readonly telemetryContent: ITelemetryContent | undefined;

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}>) {
        super(props);
        this.isAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false,
            miniCartData: {},
            viewCartEmptyMsg: true,
            preferredLanguage: 'english',
            preferredLanguageInError: 'maori',
            siteLang: 'english'
        };
    }

    public componentDidUpdate() {
        if (this.props.data.cart.result?.isEmpty) {
            setTimeout(() => {
                this.setState({
                    viewCartEmptyMsg: true
                });
            }, 1000);
        } else {
            this.setState({
                viewCartEmptyMsg: false
            });
        }
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }

        const context = this.props.context.actionContext;

        reaction(
            () => this.props.data.cart.result?.cart.CartLines,
            async () => {
                this.setState({
                    miniCartData: await getMiniCartData(context, this.props.data.cart.result!)
                });
            }
        );

        this.setState({
            orgUnitLocations: this.props.data.orgUnitLocations?.result
        });
        await this._setOrderQuantityLimitsFeatureFlag();
        this._validateCart();
        void this._getCustomerLangDetails();
    }

    private _validateCart(): void {
        const {
            data: { cart }
        } = this.props;
        const cartVersion = cart.result?.cart?.Version ?? undefined;
        if (this.lastValidatedCartVersion === cartVersion) {
            return;
        }

        this.lastValidatedCartVersion = cartVersion; // This field is populated to block concurrent requests
        validateCartAsync(this.state.isQuantityLimitsFeatureEnabled, cart, this.props.telemetry, this.props.context)
            .then((result: LineDataValidationFailure[] | undefined) => {
                this.setState({ isCartValid: !ArrayExtensions.hasElements(result) });
            })
            .catch(() => {
                this.setState({ isCartValid: false });
            });
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    private readonly _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result.removeCartLines(input).catch(error => {
                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to Remove Cart Line');
            });
        }
    };

    private readonly _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    private async _setOrderQuantityLimitsFeatureFlag(): Promise<void> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (!defaultOrderQuantityLimitsFeatureConfig || defaultOrderQuantityLimitsFeatureConfig === 'none') {
            this.setState({ isQuantityLimitsFeatureEnabled: false });
            return;
        }
        try {
            const featureStatuses = await this.props.data.featureState;
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                this.setState({ isQuantityLimitsFeatureEnabled: false });
                return;
            }
        } catch (error) {
            this.props.telemetry.warning(error as string);
            this.props.telemetry.debug('Unable to get feature states');
            this.setState({ isQuantityLimitsFeatureEnabled: false });
            return;
        }
        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            this.setState({ isQuantityLimitsFeatureEnabled: true });
        } else {
            this.props.data.customerInformation
                .then(customerInfo => {
                    const result =
                        !!customerInfo &&
                        ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                            (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b));
                    this.setState({ isQuantityLimitsFeatureEnabled: result });
                })
                .catch(error => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to get customer info');
                });
        }
    }

    /**
     * Sort cartline items.
     * @param cartlines - Cartlines to be sorted.
     * @param sortOrder - Ascending or descending.
     * @returns - Sorted cartlines.
     */
    private readonly getSortedCartLines = (cartlines: CartLine[], sortOrder?: cartLinesSortOrder): CartLine[] => {
        const lines: CartLine[] = [...cartlines];
        const zero: number = 0;

        if (sortOrder === cartLinesSortOrder.descending) {
            return lines.sort((item1, item2) => (item2.LineNumber ?? zero) - (item1.LineNumber ?? zero));
        }
        return lines.sort((item1, item2) => (item1.LineNumber ?? zero) - (item2.LineNumber ?? zero));
    };

    private _getCartLines(): IFlyoutCartLineItemViewProps[] | null {
        const {
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);
        const cart = this.props.data.cart && this.props.data.cart.result;

        // Cart-icon could be placed in each page
        // so we check cart content to reduce number of RS calls
        return this.state.miniCartData.products && cart && cart.cart.CartLines && cart.cart.CartLines.length > 0
            ? FlyoutCartLineItems({
                  cartlines: this.getSortedCartLines(cart.cart.CartLines, config.cartLinesSortOrder),
                  products: this.state.miniCartData.products,
                  productAvailabilites: this.state.miniCartData.productAvailabilites,
                  productDeliveryOptions: this.state.miniCartData.deliveryOptions,
                  imageSettings,
                  outOfStockThreshold: this.props.context.app.config.outOfStockThreshold || 0,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  resources: {
                      sizeString: resources.productDimensionTypeSize,
                      colorString: resources.productDimensionTypeColor,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      quantityDisplayString: resources.quantityDisplayText,
                      inputQuantityAriaLabel: '', // Not used
                      discountStringText: '', // Not used
                      originalPriceText: '',
                      currentPriceText: '',
                      shippingChargesText: ''
                  },
                  telemetry,
                  removeButtonText: resources.removeCartButtonText,
                  outOfStockText: '', // Not used
                  outOfRangeOneText: '', // Not used
                  outOfRangeFormatText: '', // Not used
                  cartState: cart,
                  orgUnitLocations: this.state.orgUnitLocations,
                  removeItemClickHandler: this._removeItemFromCartHandler,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  telemetryContent: this.telemetryContent,
                  channelDeliveryOptionConfig: this.retailMultiplePickUpOptionEnabled ? this.channelDeliveryOptionConfig : undefined,
                  isQuantityLimitsFeatureEnabled: this.state.isQuantityLimitsFeatureEnabled
                  //   quickViewButton: this.props.slots.quickview
              } as any)
            : null;
    }

    private _getCustomerLangDetails() {
        // Identifying Language
        const toggleLang = getToggleLang(this.props.context);
        const userPrefLang = getUserPrefLang(this.props.context.request.user.customerAccountNumber as any, this.props.context);
        this.setState({
            ...this.state,
            preferredLanguage: getUserPreferredLanguage(KWR, HEADER_FOOTER, userPrefLang, toggleLang, this.props.context),
            preferredLanguageInError: getUserPreferredLanguage(KWR, ERROR_OR_NOTIFICATION, userPrefLang, toggleLang, this.props.context),
            siteLang: getUserPreferredLanguage(KWR, COLLECTIONS_SITE_LANGUAGE, userPrefLang, toggleLang, this.props.context)
        });
    }

    private _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (msdyn365Commerce.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    }

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        const cart = this.props.data.cart.result ?? undefined;
        const {
            config: { isAnonymousCheckout },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            cartlines,
            resources,
            context,
            id,
            typeName,
            config
        } = this.props;
        const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const checkoutURL = this._getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const hasInvoiceLine = cart?.hasInvoiceLine;
        const hasError = cartlines ? cartlines.some(cartline => cartline.hasError) : false;
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        const payload = getPayloadObject(TelemetryEvent.CheckOut, telemetryContent, TelemetryConstant.Checkout);
        const checkoutBySignInAttributes = getTelemetryAttributes(telemetryContent, payload);

        payload.contentAction.etext = TelemetryConstant.GuestCheckout;
        const checkoutByGuestAttributes = getTelemetryAttributes(telemetryContent, payload);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const {} = this.props;
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }

        const cartLines = this._getCartLines();
        // if(this.props.data.cart.result?.isEmpty && !this.state.viewCartEmptyMsg) {
        //     setTimeout(() => {
        //         this.setState({
        //             viewCartEmptyMsg: true
        //         });
        //     }, 1000);
        // }
        // else {
        //     this.setState({
        //         viewCartEmptyMsg: false
        //     });
        // }

        const checkoutAsSignInUserButton =
            !isCartEmpty && !hasInvoiceLine && (!isAnonymousCheckout || isAuthenticated) ? (
                <Button
                    disabled={hasError}
                    className='ms-cart-icon__btn-checkout'
                    href={checkoutURL}
                    title={this.state.preferredLanguage === 'maori' ? resources.checkoutButtonTitleInMaori : resources.checkoutButtonTitle}
                    {...checkoutBySignInAttributes}
                >
                    {this.state.preferredLanguage === 'maori' ? resources.checkoutButtonTitleInMaori : resources.checkoutButtonTitle}
                </Button>
            ) : null;

        const checkoutAsGuestButton =
            isAnonymousCheckout && !isCartEmpty && !isAuthenticated ? (
                <Button
                    disabled={hasError}
                    className='ms-cart-icon__btn-guestcheckout'
                    href={guestCheckoutUrl}
                    title={
                        this.state.preferredLanguage === 'maori'
                            ? resources.guestCheckoutButtonTitleInMaori
                            : resources.guestCheckoutButtonTitle
                    }
                    {...checkoutByGuestAttributes}
                >
                    {this.state.preferredLanguage === 'maori'
                        ? resources.guestCheckoutButtonTitleInMaori
                        : resources.guestCheckoutButtonTitle}
                </Button>
            ) : null;

        const cartIcon = (
            <CartIconComponent
                cartLabel={this.state.preferredLanguage === 'maori' ? resources.keteLabelInMaori : resources.keteLabel}
                cartQtyLabel={resources.cartQtyLabel}
                context={context}
                id={id}
                typeName={typeName}
                data={{ cart }}
                telemetryContent={telemetryContent}
                cartIconImage={config.cartIconImage}
            />
        );
        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn'>
                    {cartIcon}
                </button>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.state.siteLang === MAORI_LANG ? this.props.config.flyoutTitleInMaori : this.props.config.flyoutTitle}
                        {/* {this._renderCartItemCount(this.props)} */}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.data.cart.result?.isEmpty && this.state.viewCartEmptyMsg ? (
                            <h4 className='msc-alert msc-alert-warning'>
                                {this.state.preferredLanguageInError === 'maori' ? resources.emptyKeteTextInMaori : resources.emptyKeteText}
                            </h4>
                        ) : null}
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(cartLines as any, this.props)}</Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {/* {this.props.slots.promoContentItem} */}
                        {/* {renderTotalPrice(this.props)} */}
                        {/* <p className='ms-cart-icon__subtotal-message'>{this.props.resources.subTotalMessage}</p> */}
                        {checkoutAsSignInUserButton}
                        {checkoutAsGuestButton}
                        {/* {this.props.data.cart.result?.isEmpty ? null : this.props.goToCartButton} */}
                        <Node className='ms-cart-icon__btn-section'>
                            {/* {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null} */}
                            <Button
                                className='moe-btn moe-btn--default-white moe-btn-continue-browsing'
                                title={
                                    this.state.preferredLanguage === 'maori'
                                        ? resources.continueBrowsingTextInMaori
                                        : resources.continueBrowsingText
                                }
                                aria-label='Close Modal'
                                onClick={this.closeModal}
                            >
                                {this.state.preferredLanguage === 'maori'
                                    ? resources.continueBrowsingTextInMaori
                                    : resources.continueBrowsingText}
                            </Button>
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = async (): Promise<void> => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        const cartState = await getCartState(this.props.context.actionContext);
        await cartState.refreshCart({});
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    // /**
    //  * Render Cart Item count.
    //  * @param props - Cart icon view props.
    //  * @returns JSX Element.
    //  */
    // private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    //     let cartItemCount;
    //     const defaultCartItemCount: number = 0;
    //     const defaultCartItemCountOne: number = 1;

    //     if (ArrayExtensions.hasElements(props.cartlines)) {
    //         if (props.cartlines.length === defaultCartItemCountOne) {
    //             cartItemCount = `${defaultCartItemCountOne} ${props.resources.item}`;
    //         } else {
    //             cartItemCount = `${props.cartlines.length} ${props.resources.items}`;
    //         }
    //     } else {
    //         cartItemCount = `${defaultCartItemCount} ${props.resources.items}`;
    //     }
    //     return (
    //         <Node className='ms-cart-icon__count' tabIndex='0'>
    //             {cartItemCount}
    //         </Node>
    //     );
    // };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };
}

export default CartIconView;
